/**
 * EinstellungenScreen — the user-settings page (Part C), reached from the app-shell
 * bottom-left profile area.
 *
 * Shows the caller's name, avatar, job title and CURRENT role (GET /me/profile).
 * Name / job title are editable (PATCH /me/profile). The avatar is changed by
 * CLICKING it: a file picker opens, the picked PNG/JPEG/WEBP is downscaled to a
 * square ~256x256 in the browser and uploaded (POST /me/avatar) — no more pasting a
 * raw avatar URL (that was bad UX and an unnecessary trust boundary). When the
 * caller is a pure read-only user (`is_read_only`), a "Höheren Zugriff anfragen"
 * button posts /me/access-request, which notifies every tenant admin (in-app +
 * Brokly-branded email).
 *
 * In mock/dev mode the profile resolves a fixture (no backend) so the screen renders
 * standalone; live mode hits the real endpoints. The avatar upload mirrors that split:
 * mock mode previews the picked image locally (FileReader → data URI, no network),
 * live mode uploads to POST /me/avatar.
 *
 * Header treatment matches the app's other main screens (Kunden/Policen/Ablage):
 * AdminPageHeader's italic display-title H1 + a subtitle line underneath, instead of
 * this page's previous plain `text-lg` heading.
 *
 * [Source: apps/web/src/features/administration/AdminPageHeader.tsx] (shared page-header pattern)
 * [Source: apps/web/src/features/administration/pages/SignaturPage.tsx] (settings form pattern)
 * [Source: apps/web/src/lib/api-client.ts#me]
 */

import { MyBlocklistSection } from '../blocklist/MyBlocklistSection';
import { PrivateSendersSection } from '../blocklist/PrivateSendersSection';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Camera, Check, Loader2, ShieldAlert } from 'lucide-react';
import type { MeProfileResponse } from '@brokly/shared';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import { runtimeConfig } from '@runtime-config';
import { Button, TextInput } from '../administration/components';
import { AdminPageHeader } from '../administration/AdminPageHeader';
import {
  blobToDataUrl,
  resizeAvatarToSquareJpeg,
  validateAvatarFile,
  type AvatarValidationError,
} from './avatar-image';

const MOCK_PROFILE: MeProfileResponse = {
  id: '00000000-0000-0000-0000-0000000000aa',
  display_name: 'Hans Muster',
  email: 'hans.muster@beispiel.ch',
  job_title: 'Kundenberater',
  avatar_url: null,
  role_names: ['basic'],
  is_read_only: false,
};

const AVATAR_ERROR_MESSAGES: Record<AvatarValidationError | 'upload_failed', string> = {
  invalid_type: 'Bitte ein PNG-, JPEG- oder WEBP-Bild wählen.',
  too_large: 'Das Bild darf höchstens 2 MB gross sein.',
  upload_failed: 'Das Profilbild konnte nicht hochgeladen werden.',
};

/** Text initials from a display name (or email), for the avatar fallback. */
function initialsOf(name: string | null, email: string): string {
  const src = (name ?? email ?? '').trim();
  if (!src) return '?';
  const parts = src.split(/\s+/).filter(Boolean);
  const letters = parts.length >= 2 ? parts[0]![0]! + parts[parts.length - 1]![0]! : src.slice(0, 2);
  return letters.toUpperCase();
}

export function EinstellungenScreen() {
  const live = isLiveData();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const profileQuery = useQuery<MeProfileResponse>({
    queryKey: qk.meProfile(),
    queryFn: () => (live ? apiClient.me.profile() : Promise.resolve(MOCK_PROFILE)),
    staleTime: live ? 60_000 : Infinity,
    ...(live ? {} : { initialData: MOCK_PROFILE }),
  });

  const [displayName, setDisplayName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [saved, setSaved] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  // Seed the form whenever a fresh profile arrives.
  useEffect(() => {
    const p = profileQuery.data;
    if (p) {
      setDisplayName(p.display_name ?? '');
      setJobTitle(p.job_title ?? '');
    }
  }, [profileQuery.data]);

  const save = useMutation({
    mutationFn: () =>
      apiClient.me.updateProfile({
        display_name: displayName.trim(),
        job_title: jobTitle.trim(),
      }),
    onSuccess: (updated) => {
      qc.setQueryData(qk.meProfile(), updated);
      setSaved(true);
    },
  });

  const uploadAvatar = useMutation({
    mutationFn: async (file: File): Promise<MeProfileResponse> => {
      const resized = await resizeAvatarToSquareJpeg(file);
      if (!live) {
        // Mock mode: preview locally (data URI), no network call — mirrors the
        // DocumentsScreen upload precedent (short-circuit off `live`).
        const dataUrl = await blobToDataUrl(resized);
        const base = profileQuery.data ?? MOCK_PROFILE;
        return { ...base, avatar_url: dataUrl };
      }
      return apiClient.me.uploadAvatar(resized);
    },
    onSuccess: (updated) => {
      qc.setQueryData(qk.meProfile(), updated);
      setAvatarError(null);
    },
    onError: () => setAvatarError(AVATAR_ERROR_MESSAGES.upload_failed),
  });

  const requestAccess = useMutation({
    mutationFn: () => apiClient.me.requestAccess(),
    onSuccess: () => setRequestSent(true),
  });

  function handleAvatarPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset the input so picking the SAME file again still fires onChange.
    e.target.value = '';
    if (!file) return;

    const validationError = validateAvatarFile(file);
    if (validationError) {
      setAvatarError(AVATAR_ERROR_MESSAGES[validationError]);
      return;
    }
    setAvatarError(null);
    uploadAvatar.mutate(file);
  }

  const profile = profileQuery.data;

  if (profileQuery.isLoading) {
    return (
      <div className="px-6 py-5">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Wird geladen …
        </p>
      </div>
    );
  }

  if (profileQuery.isError || !profile) {
    return (
      <div className="px-6 py-5">
        <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertCircle className="size-4" /> Das Profil konnte nicht geladen werden.
        </p>
      </div>
    );
  }

  const roleLabel = profile.role_names.length > 0 ? profile.role_names.join(', ') : '-';
  const avatarUrl = profile.avatar_url;

  return (
    <div className="px-6 py-5">
      <AdminPageHeader title="Einstellungen" description="Ihr Profil und Ihr Zugriff." />

      {/* Identity + editable fields — kept at a readable form width, flush-left
          with the page (no page-level centering — matches the list-page container). */}
      <div className="flex max-w-2xl flex-col gap-6">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSaved(false);
            save.mutate();
          }}
          className="flex flex-col gap-4 rounded-lg border bg-card p-4"
        >
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              aria-hidden="true"
              tabIndex={-1}
              onChange={handleAvatarPick}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Profilbild ändern"
              title="Profilbild ändern"
              disabled={uploadAvatar.isPending}
              className="group relative size-12 shrink-0 overflow-hidden rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName || profile.email}
                  className="size-12 rounded-full object-cover"
                />
              ) : (
                <div className="grid size-12 place-items-center rounded-full bg-primary/15 text-sm font-semibold text-primary">
                  {initialsOf(displayName || profile.display_name, profile.email)}
                </div>
              )}
              {/* Hover affordance + upload spinner — the click target IS the avatar. */}
              <div
                className={`absolute inset-0 flex items-center justify-center rounded-full bg-black/40 text-white transition-opacity ${
                  uploadAvatar.isPending ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}
              >
                {uploadAvatar.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Camera className="size-4" />
                )}
              </div>
            </button>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{displayName || '-'}</div>
              <div className="truncate text-xs text-muted-foreground">{profile.email}</div>
            </div>
          </div>

          {avatarError && (
            <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
              <AlertCircle className="size-4" /> {avatarError}
            </p>
          )}

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs font-medium text-muted-foreground">Name</span>
            <TextInput
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Hans Muster"
              aria-label="Name"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs font-medium text-muted-foreground">Funktion</span>
            <TextInput
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="Kundenberater"
              aria-label="Funktion"
            />
          </label>

          {save.isError && (
            <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
              <AlertCircle className="size-4" /> Das Profil konnte nicht gespeichert werden.
            </p>
          )}

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              Speichern
            </Button>
            {saved && (
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Check className="size-4" /> Gespeichert
              </span>
            )}
          </div>
        </form>

        {/* Personal blacklist: the senders this user blocked for themselves. */}
        <PrivateSendersSection />
        <MyBlocklistSection />

        {/* Current role + (read-only) request-higher-access */}
        <section className="flex flex-col gap-3 rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Aktuelle Rolle</span>
            <span className="text-sm font-medium">{roleLabel}</span>
          </div>

          {profile.is_read_only && (
            <div className="flex flex-col gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
              <p className="flex items-start gap-2 text-sm text-foreground">
                <ShieldAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />
                Sie haben aktuell nur Lesezugriff. Fordern Sie erweiterten Zugriff an, die
                Administratoren Ihres Mandanten werden benachrichtigt.
              </p>
              {requestSent || requestAccess.isSuccess ? (
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Check className="size-4" /> Anfrage gesendet.
                </p>
              ) : (
                <div>
                  <Button
                    type="button"
                    onClick={() => requestAccess.mutate()}
                    disabled={requestAccess.isPending}
                  >
                    {requestAccess.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                    Höheren Zugriff anfragen
                  </Button>
                  {requestAccess.isError && (
                    <p className="mt-2 flex items-center gap-2 text-sm text-destructive" role="alert">
                      <AlertCircle className="size-4" /> Die Anfrage konnte nicht gesendet werden.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        <p className="text-xs text-muted-foreground">
          Version {runtimeConfig('BROKLY_VERSION') ?? 'dev'}
        </p>
      </div>
    </div>
  );
}
